import React from 'react';
import { Button, StyledDiv } from '../../../atoms/button/button';
import { Checkbox } from '../../../atoms/checkbox/checkbox';
import { ExpandableCard } from '../../../molecules/expandable-card/expandable-card';
import {
  Spacing,
  Typography,
} from '../../../atoms/progress-with-label/progress-with-label';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface UGEGQuestionFormat {
  id: string;
  label: string;
}

export interface UGEGQuestionFormatSection {
  id: string;
  title: string;
  description: string;
  formats: UGEGQuestionFormat[];
}

export interface UGEGSelectQuestionFormatsProps {
  sections: UGEGQuestionFormatSection[];
  selectedFormatIds: string[];
  selectedFormatTypes: string[];
  onFormatToggle: (formatId: string, isChecked: boolean) => void;
  onFormatTypeToggle: (formatTypeId: string, isChecked: boolean) => void;
  onGenerateClick: () => void;
  isGenerating?: boolean;
  questionType?: string;
  errorMessage?: string;
  imageGenerationEnabled?: boolean;
  onImageGenerationToggle?: (enabled: boolean) => void;
}

const QUESTION_TYPES = {
  SEEN: 'seen',
  UNSEEN: 'unseen',
  BOTH: 'both',
} as const;

export const UGEGSelectQuestionFormats: React.FC<
  UGEGSelectQuestionFormatsProps
> = ({
  sections,
  selectedFormatIds,
  selectedFormatTypes,
  onFormatToggle,
  onFormatTypeToggle,
  onGenerateClick,
  isGenerating = false,
  questionType,
  errorMessage,
  imageGenerationEnabled = false,
  onImageGenerationToggle,
}) => {
  const shouldShowFormats =
    questionType === QUESTION_TYPES.UNSEEN ||
    questionType === QUESTION_TYPES.BOTH;

  const filteredSections = sections.filter((section) =>
    selectedFormatTypes.includes(section.id),
  );

  return (
    <StyledDiv flexDirection="column" padding="0 0 var(--rk-spacing-xxxxxxl) 0">
      {shouldShowFormats && (
        <StickyHeader>
          <Spacing marginBottom="lg">
            <Typography variant="textTitleMediumSurfaceVariant">
              Select Question Formats
            </Typography>
          </Spacing>

          <StyledDiv flexDirection="column" gap={8}>
            {sections.map((section) => (
              <Checkbox
                key={`type-${section.id}`}
                label={section.title}
                checked={selectedFormatTypes.includes(section.id)}
                onChange={(checked) => onFormatTypeToggle(section.id, checked)}
                data-cy={`ug-eg-format-type-checkbox-${section.id}`}
              />
            ))}
          </StyledDiv>
        </StickyHeader>
      )}

      {shouldShowFormats && selectedFormatTypes.length > 0 && (
        <Spacing margin="lg none" paddingBottom="xxxxxxl">
          {filteredSections.map((section) => (
            <Spacing key={section.id} marginBottom="md">
              <StyledExpandableCard
                title={section.title}
                defaultExpanded={true}
              >
                <Spacing padding="none lg">
                  <Typography variant="textBodySmallOnSurfaceVariant">
                    {section.description}
                  </Typography>
                </Spacing>
                <Spacing marginTop="md">
                  <StyledDiv flexDirection="column" gap={2}>
                    {section.formats.map((format) => (
                      <Checkbox
                        key={format.id}
                        label={format.label}
                        checked={selectedFormatIds.includes(format.id)}
                        onChange={(checked) =>
                          onFormatToggle(format.id, checked)
                        }
                        data-cy={`ug-eg-format-checkbox-${format.id}`}
                      />
                    ))}
                  </StyledDiv>
                </Spacing>
              </StyledExpandableCard>
            </Spacing>
          ))}
        </Spacing>
      )}

      {errorMessage && shouldShowFormats && (
        <Spacing padding="md">
          <Typography variant="textBodySmall" color="var(--md-sys-color-error)">
            {errorMessage}
          </Typography>
        </Spacing>
      )}

      <Spacing marginBottom="lg">
        <Checkbox
          label="Image Generation"
          checked={imageGenerationEnabled}
          onChange={(checked) => onImageGenerationToggle?.(checked)}
          data-cy="ug-eg-image-generation-toggle"
        />
        <Typography variant="textBodySmallOnSurfaceVariant">
          Generate B&W illustrations for picture description and diagram
          questions. Adds ~30–120s per image.
        </Typography>
      </Spacing>

      <SubmitBtn>
        <Button
          data-cy="ug-eg-generate-questions-button"
          onClick={onGenerateClick}
          disabled={
            (shouldShowFormats && selectedFormatIds.length === 0) ||
            isGenerating
          }
        >
          {isGenerating ? 'Generating...' : 'Generate Questions'}
        </Button>
      </SubmitBtn>
    </StyledDiv>
  );
};

const StickyHeader = styled.div`
  padding: var(--rk-spacing-lg) var(--rk-spacing-sm);
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--md-sys-color-on-primary);
`;

const StyledExpandableCard = styled(ExpandableCard)`
  border-bottom: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 0;
  box-shadow: none;
`;

const SubmitBtn = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
  justify-content: left;
  margin-right: var(--rk-spacing-xl);
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;

  ${mq(BreakpointSizes.xs)} {
    justify-content: center;
    md-filled-button {
      flex: 1;
    }
  }
`;
